// src/components/ui/PasswordField.tsx
import { useState, type Ref } from 'react';

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
};

/**
 * Password input with a Show/Hide toggle. Styling is passed in via
 * `className` (e.g. "underline-input" on auth screens, "ghost-input" in
 * Settings/modals) so this one component can stand in for every password
 * field in the app — LoginPage, ResetPasswordPage, SettingsPage's Change
 * Password panel, and PasswordConfirmModal.
 */
export function PasswordField({
  value,
  onChange,
  label,
  autoComplete = 'current-password',
  placeholder,
  required,
  className = 'underline-input',
  inputRef,
}: Props) {
  const [show, setShow] = useState(false);

  return (
    <label className="block">
      {label && (
        <span className="mono-label" style={{ display: 'block', marginBottom: 6 }}>
          {label}
        </span>
      )}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type={show ? 'text' : 'password'}
          required={required}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={className}
          placeholder={placeholder}
          style={{ paddingRight: 56, width: '100%' }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: 'rgba(var(--fg-rgb),0.55)',
            cursor: 'pointer',
            fontSize: 12,
            fontFamily: 'var(--font-jetbrains-mono)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </label>
  );
}
